import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import generateMenu from 'stores/business/protocolExplorerMenuBusiness/protocolMenuGenerator';
import {getSelectedProtocolsCondition} from 'stores/business/protocolExplorerMenuBusiness/protocolExplorerMenuBusiness';
import './ProtocolExplorer.scss';
import {useIntl} from 'react-intl';
import {isRoleLevel} from 'stores/business/userManagementBusiness';
import {UserRoleLevel} from 'constants/dataEnum';
import {compact} from 'lodash';
import {extractProtocolExplorerContextMenuData} from '../../lib/explorerHelper';
import {getIsOperator} from '../../stores/selectors/sessionSelectors';
import {
  getProtocolClipboardEventType,
  getProtocolClipboardItems,
  selectProtocolExplorerDefaultViewSettings,
} from '../../stores/selectors/protocolExplorerSelectors';
import FastContextMenu from '../../components/FastContextMenu/FastContextMenu';

function ProtocolContextMenu({
  menuList,
  onContextMenu,
  protocolItemsRef,
  // TODO: all handlers and checkers should be moved to business, not being handled from protocolExplorer component
  listOfSharedUsers,
  canUserPasteProtocolsFolders,
  onRenameClick,
  onShareProtocol,
  onSendProtocol,
  onCutClick,
  onCopyClick,
  onPasteClick,
  onDownloadClick,
  onDeleteClick,
  onConvertTessaraProtocol,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const isOperator = useSelector(getIsOperator);
  const protocolClipboardItems = useSelector(getProtocolClipboardItems);
  const protocolClipboardEventType = useSelector(getProtocolClipboardEventType);
  const {explorerViewMode} = useSelector(selectProtocolExplorerDefaultViewSettings);

  const [isCtxMenuOpened, setIsCtxMenuOpened] = useState(false);
  const [isWaitingForCallbackUpdates, setIsWaitingForCallbackUpdates] = useState(false);

  const isTessaraSuperUser = dispatch(isRoleLevel(UserRoleLevel.TESSARA_SUPER_USER));

  const clipboardProtocolIds = useMemo(
    () => (protocolClipboardEventType ? protocolClipboardItems.map((e) => e.ID) : []),
    [protocolClipboardEventType, protocolClipboardItems],
  );

  const menuItemsList = useRef([]);

  const generateCtxMenu = useCallback(() => {
    // protocol condition should not be separated from the context menu generator (generateMenu func).
    // This is currently separated to accommodate other context menus that hasn't been moved to business because it is not fit within the sprint timeline.
    // TODO: all handlers and checkers should be moved to business, not being handled from protocolExplorer component
    const protocolConditions = dispatch(getSelectedProtocolsCondition());

    const {
      selectedItemIds,
      selectedItems,
      isSelectingNothing,
      isContainingTessaraProtocol,
      isContainingSharedItems,
      isContainingFolder,
      isContainingCurrentlyExecutingItem,
      isContainingFavoriteGroupItem,
      isSelectingMixedRegularTessaraProtocol,
    } = protocolConditions || {};

    const isSameListofUserForProtocols = selectedItems.every((p) => {
      if (p?.[p.type]?.isTessaraProtocol) return false;
      return listOfSharedUsers.every((t) => p?.[p.type].sharedTo.includes(t));
    });

    const generatedMenu = dispatch(generateMenu(menuList, selectedItemIds));

    const menu = isOperator
      ? []
      : [
          ...generatedMenu,
          {
            label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.renameOption'}),
            icon: 'Rename',
            onClick: onRenameClick,
            disabled: selectedItems?.length !== 1 || isContainingCurrentlyExecutingItem || isContainingSharedItems,
          },
          {
            label: intl.formatMessage({id: 'explorer.explorerHeader.menuShare'}),
            icon: 'SendACopy',
            onClick: onSendProtocol,
            disabled: isSelectingNothing || isContainingSharedItems || isContainingTessaraProtocol,
          },
          {
            label: intl.formatMessage({id: 'explorer.explorerHeader.menuShare.context'}),
            icon: 'FastFolderShared',
            onClick: onShareProtocol,
            disabled:
              isSelectingNothing ||
              !isSameListofUserForProtocols ||
              isContainingCurrentlyExecutingItem ||
              isContainingSharedItems,
          },
          {
            label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.cutProtocolFile'}),
            icon: 'Cut',
            onClick: onCutClick,
            disabled:
              isSelectingNothing ||
              isContainingCurrentlyExecutingItem ||
              isContainingFavoriteGroupItem ||
              (isContainingSharedItems && !isContainingTessaraProtocol),
          },
          {
            label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.copyProtocolFile'}),
            icon: 'FileCopy',
            onClick: onCopyClick,
            disabled: isSelectingNothing || isContainingSharedItems,
          },
          {
            label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.pasteProtocolFile'}),
            icon: 'FilePaste',
            onClick: onPasteClick,
            disabled: !clipboardProtocolIds || !clipboardProtocolIds.length || !canUserPasteProtocolsFolders,
          },
          isTessaraSuperUser && {
            label: intl.formatMessage({
              id: `explorer.explorerHeader.menuMore.${isContainingTessaraProtocol ? 'convertToRegularProtocol' : 'convertToTessaraProtocol'}.context`,
            }),
            icon: isContainingTessaraProtocol ? 'Protocol' : 'FastTessara',
            onClick: onConvertTessaraProtocol,
            disabled:
              isSelectingNothing ||
              isContainingCurrentlyExecutingItem ||
              isContainingFolder ||
              isSelectingMixedRegularTessaraProtocol,
          },
          {
            label: intl.formatMessage({id: 'explorer.explorerHeader.menuMore.downloadOption'}),
            icon: 'Download',
            onClick: onDownloadClick,
            disabled: isSelectingNothing || isContainingSharedItems,
          },
          {
            label: intl.formatMessage({id: 'explorer.explorerHeader.menuRemove'}),
            icon: 'Trash',
            onClick: onDeleteClick,
            disabled: isSelectingNothing || isContainingSharedItems || isContainingCurrentlyExecutingItem,
          },
        ];

    return compact(menu);
  }, [
    canUserPasteProtocolsFolders,
    clipboardProtocolIds,
    dispatch,
    intl,
    isOperator,
    isTessaraSuperUser,
    listOfSharedUsers,
    menuList,
    onConvertTessaraProtocol,
    onCopyClick,
    onCutClick,
    onDeleteClick,
    onDownloadClick,
    onPasteClick,
    onRenameClick,
    onSendProtocol,
    onShareProtocol,
  ]);

  useEffect(() => {
    if (isWaitingForCallbackUpdates) {
      menuItemsList.current = generateCtxMenu();

      setIsCtxMenuOpened(true);
      setIsWaitingForCallbackUpdates(false);
    }
  }, [generateCtxMenu, isWaitingForCallbackUpdates]);

  const handleOpenContextMenu = async (target) => {
    if (isOperator || !target) return;

    // close context menu first when changing position
    // and add await to avoid previous menu items being shown before menu is opened in new position
    if (isCtxMenuOpened) {
      await setIsCtxMenuOpened(false);
    }

    const {protocolId, itemIndex, isSelected} = extractProtocolExplorerContextMenuData(target, explorerViewMode);
    onContextMenu({protocolId, itemIndex, isSelected});

    // TODO: no need to wait for callback props update if handlers are all in business :(
    setIsWaitingForCallbackUpdates(true);
  };

  const handleCloseContextMenu = () => {
    setIsCtxMenuOpened(false);
  };

  return (
    <FastContextMenu
      open={isCtxMenuOpened}
      contextMenuItems={menuItemsList.current}
      elementsRef={protocolItemsRef}
      onContextMenu={handleOpenContextMenu}
      onClose={handleCloseContextMenu}
    />
  );
}

ProtocolContextMenu.propTypes = {
  menuList: PropTypes.array.isRequired,
  onContextMenu: PropTypes.func.isRequired,
  protocolItemsRef: PropTypes.any.isRequired,
  // TODO: all handlers should be moved to business, not being handled from protocolExplorer component
  listOfSharedUsers: PropTypes.array.isRequired,
  canUserPasteProtocolsFolders: PropTypes.bool.isRequired,
  onRenameClick: PropTypes.func.isRequired,
  onShareProtocol: PropTypes.func.isRequired,
  onSendProtocol: PropTypes.func.isRequired,
  onCutClick: PropTypes.func.isRequired,
  onCopyClick: PropTypes.func.isRequired,
  onPasteClick: PropTypes.func.isRequired,
  onDownloadClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onConvertTessaraProtocol: PropTypes.func.isRequired,
};

export default ProtocolContextMenu;
